import type { ApiResponse } from '@/common/services/api.service-new';
import { ALERT_CADENCE_MS, checkedAtOf, refreshedAtOf } from '../alerts/alert-freshness';
import { ZoneBatchClient } from '../alerts/zone-batch.client';
import type { ChunkOutcome, ZoneAlertResponse } from '../alerts/zone-batch.client';
import type { AlertsRaddZone, DeforestationLayerChoice } from './deforestation.types';

type Response = ZoneAlertResponse<AlertsRaddZone>;

const failure = (message: string, status?: number): Response => ({
  data: null,
  error: { message, status },
  isSuccess: false,
});
/** `raw` is the entry the payload came from, which carries `refreshed_at`. */
const success = (data: AlertsRaddZone, raw: unknown): Response => ({
  data,
  error: null,
  isSuccess: true,
  refreshedAt: refreshedAtOf(raw),
  checkedAt: checkedAtOf(raw),
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Batch reads for one deforestation product's per-zone alert summaries.
 *
 * One instance per layer, because the layer is the path segment
 * (`/radd/zones`, `/glad/zones`, `/glad-radd/zones`) and the cache is keyed by
 * layer as well as zone. Keeping one client per layer means the inherited
 * in-flight map is keyed by zone id alone and still cannot collide across
 * products.
 *
 * Contract: `alerts-batch-by-zone-ids`. The batch is **cache-only**, and says so
 * per zone with a three-state `status`:
 *
 * | status | meaning | what we do |
 * | --- | --- | --- |
 * | `ok` | row exists, alerts found | use the body |
 * | `empty` | row exists, genuinely zero | success with no content, as the 204 |
 * | `uncomputed` | never computed for this zone | fall back to the single-zone route |
 *
 * `uncomputed` falling back is the point, not an optimisation. The single-zone
 * route live-computes on a miss, so today every zone eventually gets a real
 * answer. Rendering `uncomputed` as zero would be wrong, and rendering it as
 * unknown would be a regression against the fan-out this replaces.
 */
export class DeforestationAlertsClient extends ZoneBatchClient<AlertsRaddZone> {
  constructor(
    private readonly get: (path: string) => Promise<ApiResponse<AlertsRaddZone>>,
    private readonly layer: DeforestationLayerChoice,
    cached: (id: number) => Response | null | undefined,
    cache: (id: number, response: Response) => void,
  ) {
    super(cached, cache);
  }

  protected get cadenceMs(): number {
    return ALERT_CADENCE_MS.deforestation;
  }

  /** `uncomputed` never reaches the cache: it falls back to the route that computes it. */
  protected isComputed(): boolean {
    return true;
  }

  /**
   * Never batch a lone zone.
   *
   * For one id the cache-only batch is a strict subset of `GET /{layer}/zone/{id}`,
   * because that route also computes a miss. So the batch can only tell us to go
   * ask the singular route anyway — a wasted round trip on `radd`, and a
   * guaranteed one on `glad` / `glad-radd`, where `uncomputed` is the normal
   * answer. Fire is the opposite case and keeps the inherited 1; see
   * `ZoneBatchClient.minBatchIds`.
   */
  protected get minBatchIds(): number {
    return 2;
  }

  protected failure(message: string, status?: number): Response {
    return failure(message, status);
  }
  protected get failureMessage(): string {
    return 'Deforestation alerts request failed';
  }
  protected get missingMessage(): string {
    return 'Deforestation alerts response missing';
  }

  protected async fetchChunk(ids: number[]): Promise<ChunkOutcome<AlertsRaddZone>> {
    const response = await this.get(`/${this.layer}/zones?ids=${ids.join(',')}`);
    if (!response.isSuccess) {
      // A cap this client has not learned yet: the server's limit is
      // env-overridable and we only ever read `max_ids` off a SUCCESS, so
      // without this the same oversized batch is refused on every page load.
      // Narrow, then defer these ids to the per-zone route so the user still
      // gets numbers now; the next call chunks to the cap just learned.
      const namedCap = this.capFromRejection(response.error);
      if (namedCap !== undefined) {
        this.noteServerCap(namedCap);
        return { results: new Map(), fallbackIds: ids };
      }
      // An absent route means the batch has been rolled back; anything else is
      // a real failure and must not be retried as N requests.
      if (response.error?.status === 404 || response.error?.status === 405) {
        return { results: new Map(), fallbackIds: ids };
      }
      return { results: new Map(ids.map((id) => [id, { ...response, data: null }])) };
    }

    const body: unknown = response.data;
    if (!isRecord(body) || !isRecord(body.zones)) {
      return { results: new Map(), fallbackIds: ids };
    }
    this.noteServerCap(body.max_ids);
    const zones = body.zones;

    const results = new Map<number, Response>();
    const fallbackIds: number[] = [];
    for (const id of ids) {
      // Omission is not "no alerts". The producer guarantees every requested id
      // appears, so an absent key means we cannot say, not that the count is 0.
      if (!Object.prototype.hasOwnProperty.call(zones, String(id))) {
        fallbackIds.push(id);
        continue;
      }
      const entry = zones[String(id)];
      if (!isRecord(entry)) {
        fallbackIds.push(id);
        continue;
      }
      if (entry.status === 'ok') {
        results.set(id, success(entry as unknown as AlertsRaddZone, entry));
      } else if (entry.status === 'empty') {
        // Mirrors the single-zone 204: a success carrying no body, which the
        // store caches so the empty case stops re-hitting the Lambda.
        // `refreshed_at` is carried on the response, since there is no body to
        // carry it: an empty result still has an age.
        results.set(id, {
          data: null,
          error: null,
          isSuccess: true,
          isNoContent: true,
          refreshedAt: refreshedAtOf(entry),
          checkedAt: checkedAtOf(entry),
        });
      } else {
        // `uncomputed`, or a status we do not recognise. Either way the single
        // -zone route is the one that can still produce an answer.
        fallbackIds.push(id);
      }
    }

    return { results, fallbackIds };
  }

  protected async fetchSingular(id: number): Promise<Response> {
    try {
      // The layer choice doubles as the endpoint path segment.
      const response = await this.get(`/${this.layer}/zone/${id}`);
      if (!response.isSuccess) {
        return { ...response, data: null };
      }
      // A 204 is bodiless, so its age is unknown.
      return { ...response, refreshedAt: refreshedAtOf(response.data), checkedAt: checkedAtOf(response.data) };
    } catch (error) {
      return failure(error instanceof Error ? error.message : this.failureMessage);
    }
  }
}
